import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Button,
  useDisclosure,
  useToast,
  Spinner,
  Center,
  HStack,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MdArrowBack } from 'react-icons/md'
import { IconButton } from '@chakra-ui/react'
import PricingCard from '../../components/Subscription/PricingCard'
import EnterpriseFormModal from '../../components/Subscription/EnterpriseFormModal'
import { SubscriptionPlan, PlanType, EnterpriseFormData } from '../../types/subscription'
import { useSelectPlan, useSubmitEnterpriseRequest } from '../../api/api_hooks/subscription'
import { useMyProfileWithModules } from '../../api/api_hooks/user/myProfile'
import { useQueryClient } from '@tanstack/react-query'
import { ModuleType as BackendModuleType } from '../../types/modules'

export default function ChangePlan() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const queryClient = useQueryClient()

  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const { data: user, isLoading: isLoadingUser } = useMyProfileWithModules()
  const { mutateAsync: selectPlan } = useSelectPlan()
  const { mutateAsync: submitEnterpriseRequest } = useSubmitEnterpriseRequest()

  const currentUserPlan = user?.plan

  // A collaborateur invited by another pilot has their plan inherited from
  // that pilot (see canChangePlan, GET /users/profile) — the button that
  // leads here is already hidden on /my-info for them, but a direct URL
  // visit must be blocked too, not just the entry point.
  useEffect(() => {
    if (!isLoadingUser && user && user.canChangePlan === false) {
      toast({
        title: "Vous n'avez pas le droit de changer d'offre",
        description: 'Votre offre est liée à celle de la personne qui vous a invité(e) — contactez-la pour en changer.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      })
      navigate('/my-info', { replace: true })
    }
  }, [isLoadingUser, user, navigate, toast])

  const currentUserModules = useMemo(() => {
    return user?.modules
      ?.filter((m) => m.isEnabled && m.moduleType !== BackendModuleType.PATIENTS)
      ?.map((m) => m.moduleType) || []
  }, [user?.modules])

  const getPlans = (): SubscriptionPlan[] => [
    {
      id: PlanType.PRO,
      name: 'Essentiel',
      price: 690,
      currency: '€',
      billingPeriod: 'month',
      description: 'Workflow assisté IRIS (détection, identifiants, témoin) avec validation manuelle.',
      features: [
        'Un seul opérateur',
        'Modules ALMA et IRIS (immunoblot, validation manuelle)',
        'Détection automatique des bandelettes + reconnaissance des identifiants (OCR)',
        'IRIS — calibration manuelle du témoin positif',
        '200 crédits IA/mois — 1 crédit = 1 image ALMA ou 1 bandelette',
        'Validation opérateur avec traçabilité',
        'Exports PDF, CSV, JSON et FHIR (tracés par identifiant)',
        'Commentaires, annotations, stockage illimité · support prioritaire',
      ],
      ctaText: 'Choisir Essentiel',
      badge: 'Pour démarrer',
    },
    {
      id: PlanType.PREMIUM,
      name: 'Premium',
      price: 990,
      currency: '€',
      billingPeriod: 'month',
      description: "Interprétation IA automatique pour un gain de temps clinique immédiat.",
      features: [
        'Tout ce qui est inclus dans Essentiel',
        'Collaboratif — plusieurs opérateurs',
        'Crédits IA illimités',
        'IRIS — interprétation IA des immunoblots (verdict par bandelette)',
        'IRIS — calibration automatique du témoin positif',
        'IRIS — comparaison de profils et scoring différentiel témoin/échantillon',
        'Contrôle qualité',
        'Détection automatique ALMA (parasitologie IA)',
        'Partage de cas et collaboration · Aletheia (assistant IA médical)',
        'Accès anticipé aux nouveaux modules IA',
      ],
      recommended: true,
      ctaText: 'Choisir Premium',
      badge: '✦ IA la plus avancée',
    },
    {
      id: PlanType.ENTERPRISE,
      name: 'Institution',
      price: null,
      currency: '€',
      billingPeriod: null,
      description: 'Solution sur-mesure pour CHU, laboratoires et intégrateurs.',
      features: [
        'Tout ce qui est inclus dans Premium',
        'Intégration SIH (HL7, FHIR, LIMS, DPI)',
        'Déploiement multi-sites et multi-services',
        'Développement de modules spécifiques',
        'Conformité HDS et accompagnement RGPD',
        'Gestionnaire de compte dédié',
        'SLA personnalisé et formation équipe',
        'Tarification au volume',
      ],
      ctaText: "Contacter l'équipe",
      badge: 'Sur devis',
    },
  ]

  const PLANS = getPlans()

  const defaultModules = (planId: PlanType) =>
    planId === PlanType.PREMIUM || planId === PlanType.ENTERPRISE
      ? ['lab', 'teleexpertise', 'medinsight']
      : ['lab']

  const handlePlanSelect = async (planId: PlanType) => {
    if (planId === PlanType.ENTERPRISE) { onOpen(); return }
    setIsProcessing(true)
    setSelectedPlan(planId)
    try {
      const modules = currentUserModules.length > 0 ? currentUserModules : defaultModules(planId)
      await selectPlan({ plan: planId, modules })
      localStorage.setItem('userPlan', planId)
      localStorage.setItem('userModules', JSON.stringify(modules))
      await queryClient.refetchQueries()
      toast({ title: 'Plan mis à jour', status: 'success', duration: 3000, isClosable: true })
      setTimeout(() => navigate('/home'), 500)
    } catch (_e) {
      toast({ title: 'Échec de la mise à jour', status: 'error', duration: 5000 })
      setSelectedPlan(null)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleEnterpriseSubmit = async (formData: EnterpriseFormData) => {
    await submitEnterpriseRequest({ ...formData, modules: defaultModules(PlanType.ENTERPRISE) })
    setSelectedPlan(PlanType.ENTERPRISE_PENDING)
    localStorage.setItem('userPlan', PlanType.ENTERPRISE_PENDING)
    await queryClient.refetchQueries()
    setTimeout(() => navigate('/home'), 500)
  }

  if (isLoadingUser) {
    return <Center h="100vh" bg="gray.50"><Spinner size="xl" color="brand.purple" thickness="4px" /></Center>
  }

  return (
    <Box minH="100vh" bg="gray.50" overflowY="auto">
      {/* Top bar */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={6} py={3}>
        <HStack justify="space-between">
          <HStack spacing={2} align="center">
            <IconButton
              aria-label="Retour"
              icon={<MdArrowBack />}
              variant="ghost"
              colorScheme="gray"
              size="sm"
              borderRadius="lg"
              onClick={() => navigate(-1)}
            />
            <Text fontSize="sm" color="gray.500">Retour</Text>
          </HStack>
          {currentUserPlan && currentUserPlan !== PlanType.FREE && (
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500">Offre actuelle :</Text>
              <Badge
                colorScheme={currentUserPlan === PlanType.PREMIUM ? 'orange' : currentUserPlan === PlanType.ENTERPRISE ? 'purple' : 'teal'}
                px={3} py={1} borderRadius="full" fontSize="xs"
              >
                {currentUserPlan === PlanType.PRO ? 'Essentiel' :
                 currentUserPlan === PlanType.PREMIUM ? 'Premium' :
                 currentUserPlan === PlanType.ENTERPRISE ? 'Institution' : 'Institution (en attente)'}
              </Badge>
            </HStack>
          )}
        </HStack>
      </Box>

      <Box maxW="1100px" mx="auto" px={6} py={12}>
        {/* Header */}
        <VStack spacing={3} mb={12} textAlign="center">
          <Heading size="2xl" fontWeight="extrabold" color="gray.900">
            Nos offres
          </Heading>
        </VStack>

        {/* Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} alignItems="start">
          {PLANS.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              isSelected={selectedPlan === plan.id || (currentUserPlan === plan.id && !selectedPlan)}
              onSelect={() => handlePlanSelect(plan.id)}
              isLoading={isProcessing && selectedPlan === plan.id}
            />
          ))}
        </SimpleGrid>

        <Text fontSize="xs" color="gray.400" textAlign="center" mt={10}>
          Tous les prix sont HT · par professionnel · résiliable à tout moment
        </Text>
      </Box>

      <EnterpriseFormModal isOpen={isOpen} onClose={onClose} onSubmit={handleEnterpriseSubmit} />
    </Box>
  )
}
